import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { uploadAPI } from '../../services/uploadAPI';

export interface UploadFile {
  id: string;
  name: string;
  size: number;
  progress: number;
  status: 'pending' | 'uploading' | 'encrypting' | 'completed' | 'error';
  error?: string;
  encrypted: boolean;
}

// SECURITY: encryptionPassword is intentionally NOT stored in Redux state.
// Persisting a passphrase in the global store risks exposure via Redux DevTools,
// serialized state snapshots, and logging.  The passphrase is kept in
// component-local state (useRef / useState) and passed as a function argument
// to the upload flow. (supportlogsio-1-196 L-1)
interface UploadState {
  sessionId: string | null;
  caseNumber: string | null;
  files: UploadFile[];
  isEncryptionEnabled: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: UploadState = {
  sessionId: null,
  caseNumber: null,
  files: [],
  isEncryptionEnabled: false,
  loading: false,
  error: null,
};

export const createUploadSession = createAsyncThunk(
  'upload/createSession',
  async (caseNumber: string) => {
    const response = await uploadAPI.createSession(caseNumber);
    return response.data;
  }
);

export const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    addFile: (state, action: PayloadAction<{
      id: string;
      name: string;
      size: number;
    }>) => {
      state.files.push({
        ...action.payload,
        progress: 0,
        status: 'pending',
        encrypted: state.isEncryptionEnabled,
      });
    },
    updateFileProgress: (state, action: PayloadAction<{
      id: string;
      progress: number;
    }>) => {
      const file = state.files.find(f => f.id === action.payload.id);
      if (file) {
        file.progress = action.payload.progress;
      }
    },
    updateFileStatus: (state, action: PayloadAction<{
      id: string;
      status: UploadFile['status'];
      error?: string;
    }>) => {
      const file = state.files.find(f => f.id === action.payload.id);
      if (file) {
        file.status = action.payload.status;
        file.error = action.payload.error;
      }
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.files = state.files.filter(f => f.id !== action.payload);
    },
    setEncryptionEnabled: (state, action: PayloadAction<boolean>) => {
      state.isEncryptionEnabled = action.payload;
    },
    resetUpload: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUploadSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUploadSession.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionId = action.payload.id;
        state.caseNumber = action.payload.caseNumber;
      })
      .addCase(createUploadSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create upload session';
      });
  },
});

export const {
  addFile,
  updateFileProgress,
  updateFileStatus,
  removeFile,
  setEncryptionEnabled,
  resetUpload,
} = uploadSlice.actions;

export default uploadSlice.reducer;